import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { SearchHandlerContext } from 'components/search/SearchContext';
import { SearchQueryStringParams } from 'components/search/utils';
import { calendarSelector, offeringsSelector } from 'selectors/calendarSelectors';
import { consumer } from '@tock/coretypes';
import { getNextRelease } from 'utils/businessUtils';
import { lockSelector } from 'selectors/lockSelectors';
import { parse } from '@tock/query-string';
import { useActions } from '@tock/shared/utils/connect';
import { useApp } from 'selectors/appSelector';
import { useHistory } from 'react-router-dom';
import { useQueryStringParams } from 'hooks/useQueryStringParams';
import { useSelector } from 'react-redux';
import ExperienceModal from 'components/business/ExperienceModal';

interface ExperienceModalQuery {
  date?: string;
  time?: string;
  size?: string;
  seatingAreaId?: number;
}

const SearchExperienceModal: FunctionComponent = () => {
  const queryStringParams = useQueryStringParams<SearchQueryStringParams>();
  const { getUrlPostfix, lockTimeAndSize } = { ...useContext(SearchHandlerContext) };

  const [isSearchingInModal, setIsSearchingInModal] = useState(false);

  const [experienceModalQuery, setExperienceModalQuery] = useState<ExperienceModalQuery>({
    size: queryStringParams.size,
    date: queryStringParams.date,
    time: queryStringParams.time,
    seatingAreaId: Number(queryStringParams.seatingAreaId),
  });

  const { actions } = useActions();
  const app = useApp();
  const calendar = useSelector(calendarSelector);
  const lock = useSelector(lockSelector);
  const offerings = useSelector(offeringsSelector);
  const history = useHistory();

  const experienceId = Number(queryStringParams.experienceId);

  const isOpen = queryStringParams.showModal === 'experience';

  useEffect(() => {
    setExperienceModalQuery({
      size: queryStringParams.size,
      date: queryStringParams.date,
      time: queryStringParams.time,
      seatingAreaId: Number(queryStringParams.seatingAreaId),
    });
  }, [queryStringParams]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSearchInModal = (routeObject: { search: any; pathname: string }) => {
    const query = parse<{
      date: string;
      time: string;
      size: string;
      seatingAreaId: number;
    }>(routeObject.search);
    setIsSearchingInModal(true);

    // Note: We are simulating loading here to give a visual indication that the
    // search results have changed. 17ms ~= 1 frame at 60 FPS, enough to be
    // noticable and to make sure React doesn't short-circuit the render.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setExperienceModalQuery(query);
        setIsSearchingInModal(false);
      });
    });
  };

  const isAuthenticated = !!app.jwtToken;
  const nextRelease = getNextRelease(app.config);
  const business = app.config?.business as consumer.BusinessConfig | undefined;
  const isDataLoading = calendar.offeringsInProgress || app.configRequestInProgress;
  return getUrlPostfix && lockTimeAndSize ? (
    <ExperienceModal
      business={business}
      calendar={calendar}
      offerings={offerings}
      experienceId={experienceId}
      getUrlPostfix={getUrlPostfix}
      isAuthenticated={isAuthenticated}
      isSearching={isSearchingInModal}
      isWidget={false}
      isRoute={false}
      isOpen={isOpen}
      lock={lock}
      isLoading={isDataLoading}
      nextRelease={nextRelease ?? undefined}
      onClose={() => history.goBack()}
      onSearch={handleSearchInModal}
      onSelectTime={lockTimeAndSize}
      onShowError={actions.toast.showError}
      query={experienceModalQuery as Dictionary<string>}
    />
  ) : null;
};
export default SearchExperienceModal;
